import React from "react";
import { offlineStrings } from "../offline/offlineStrings";
import useIsOffline from "../../../hooks/useIsOffline";

export type SectionSaveResetProps = {
  isDirty: boolean;
  saving: boolean;
  saveDisabled?: boolean;
  allowUnchangedSave?: boolean;
  allowUnchangedReset?: boolean;
  lastSavedLabel: string;
  onSave: () => void | Promise<void>;
  onReset: () => void;
  statusText?: string | null;
  errorText?: string | null;
  variant?: "default" | "design";
  layout?: "stacked" | "inline";
  /** Design variant only: "strong" = 13px #0A0A0A Last Saved line (Automated Emails cards). */
  lastSavedTone?: "muted" | "strong";
};

const SectionSaveReset = ({
  isDirty,
  saving,
  saveDisabled = false,
  allowUnchangedSave = false,
  allowUnchangedReset = false,
  lastSavedLabel,
  onSave,
  onReset,
  statusText,
  errorText,
  variant = "default",
  layout = "stacked",
  lastSavedTone = "muted",
}: SectionSaveResetProps) => {
  // Offline design A: Save is off until the connection is back, and the
  // status line says why. Typed changes stay on the page.
  const isOffline = useIsOffline();
  const design = variant === "design";
  const strongStatus = design && lastSavedTone === "strong";
  const disabled = (!isDirty && !allowUnchangedReset) || saving;
  const buttonBase =
    "inline-flex items-center justify-center gap-2 rounded-lg border px-5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cpaGold";
  const designButtonStyle = design
    ? { gap: 8, borderRadius: 8, padding: layout === "inline" ? "11px 20px" : "12px 20px", fontSize: 14 }
    : undefined;
  const status = (
    <p
      role={errorText ? "alert" : "status"}
      style={{
        ...(design ? { fontSize: strongStatus ? 13 : 14, lineHeight: "20px" } : {}),
        // A long web address in an error (Invoice Footer Notes refusal) wraps
        // inside the Save button column instead of running past the card.
        ...(errorText ? { overflowWrap: "anywhere" as const } : {}),
      }}
      className={`${design ? "text-sm" : "mt-2 max-w-full text-center text-sm sm:text-right"} leading-5 ${errorText ? "text-red-600" : strongStatus ? "text-[#0A0A0A]" : design ? "text-[#727272]" : statusText ? "whitespace-nowrap text-gray-900" : "whitespace-nowrap text-gray-500"}`}
    >
      {errorText ||
        (isOffline ? offlineStrings.saveOffText : null) ||
        statusText ||
        lastSavedLabel}
    </p>
  );
  return (
    <div
      style={design ? { gap: 8, paddingTop: layout === "inline" ? 0 : 16 } : undefined}
      className={
        design
          ? `flex flex-col items-end gap-2 ${layout === "inline" ? "shrink-0" : "border-t border-[#ECECEC] pt-4"}`
          : "mt-6"
      }
    >
      <div
        style={design ? { gap: 10 } : undefined}
        className={
          design
            ? "flex flex-wrap justify-end gap-2.5"
            : "flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-end"
        }
      >
        <button
          type="button"
          disabled={disabled}
          onClick={onReset}
          style={designButtonStyle}
          className={`${buttonBase} ${design ? "border-[#9E9E9E] bg-white py-3 text-[#0A0A0A] hover:bg-[#F7F7F8]" : "h-11 w-full !px-4 border-gray-500 bg-white text-gray-600 hover:bg-gray-50 sm:w-[180px]"}`}
        >
          Reset
        </button>
        <div
          className={
            design
              ? "contents"
              : "flex w-full flex-col sm:w-[280px] sm:items-end"
          }
        >
          <button
            type="button"
            disabled={
              saving ||
              saveDisabled ||
              isOffline ||
              (!isDirty && !allowUnchangedSave)
            }
            onClick={onSave}
            style={designButtonStyle}
            className={`${buttonBase} ${design ? "border-[#E1A731] bg-[#E1A731] py-3 text-white hover:bg-[#C6912A]" : "h-11 w-full !px-4 border-cpaGold bg-cpaGold text-white hover:bg-amber-500"}`}
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
          {!design && status}
        </div>
      </div>
      {design && status}
    </div>
  );
};

export default SectionSaveReset;
